import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Camera, ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useRecentEventMemories } from "@/hooks/useEventMutations";
import { cn } from "@/lib/utils";
import type { EventMemoryRow } from "@/lib/types";

const AUTO_ADVANCE_MS = 4500;

/**
 * Dashboard widget — compact slideshow of recent event memories.
 *
 * Sits in a side column, not full-width: ~one third of the dashboard
 * width on lg+. Each slide is one memory photo at a time, auto-cycling
 * every ~4.5 s with pause-on-hover. Built deliberately small so the
 * events spotlight stays the headline surface; this is a quiet "fresh
 * snapshots" panel next to it.
 *
 * The backend's `recent_memories` endpoint only ever returns rows that
 * are real EventMemory records — events with no photos contribute
 * nothing, so the slideshow only ever shows real content.
 */
export function EventMemoriesCard() {
  const memories = useRecentEventMemories(12);
  const items    = memories.data ?? [];

  const [idx,    setIdx]    = useState(0);
  const [paused, setPaused] = useState(false);

  // Reset when the feed list changes shape (e.g. a memory was added).
  useEffect(() => {
    if (idx >= items.length) setIdx(0);
  }, [items.length, idx]);

  useEffect(() => {
    if (items.length < 2 || paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), AUTO_ADVANCE_MS);
    return () => clearInterval(t);
  }, [items.length, paused]);

  if (memories.isLoading) {
    return (
      <Card className="border-border/60 shadow-soft">
        <CardHeader className="border-b border-border/60 py-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Camera className="h-3.5 w-3.5 text-muted-foreground" />Memories
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3">
          <Skeleton className="aspect-[4/3] w-full" />
        </CardContent>
      </Card>
    );
  }
  if (items.length === 0) return null;

  const active  = items[idx];
  const hasMany = items.length > 1;

  return (
    <Card
      className="border-border/60 shadow-soft"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 border-b border-border/60 py-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Camera className="h-3.5 w-3.5 text-muted-foreground" />Memories
          </CardTitle>
          <p className="text-[11px] text-muted-foreground">
            Snapshots from past events
          </p>
        </div>
        {hasMany && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-mono tabular-nums text-muted-foreground">
            {idx + 1} / {items.length}
          </span>
        )}
      </CardHeader>

      <CardContent className="p-3">
        <div className="relative aspect-[4/3] overflow-hidden rounded-md border border-border bg-muted/30">
          {/* Crossfade slide stack. */}
          {items.map((m, i) => (
            <Slide key={m.id} memory={m} visible={i === idx} />
          ))}

          {/* Carousel chrome — slim, only when >1 item. */}
          {hasMany && (
            <>
              <button
                type="button"
                onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)}
                aria-label="Previous memory"
                className="absolute left-1.5 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white opacity-0 backdrop-blur transition group-hover:opacity-100 hover:bg-black/65"
                style={{ opacity: paused ? 1 : undefined }}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setIdx((i) => (i + 1) % items.length)}
                aria-label="Next memory"
                className="absolute right-1.5 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white opacity-0 backdrop-blur transition group-hover:opacity-100 hover:bg-black/65"
                style={{ opacity: paused ? 1 : undefined }}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>

              {/* Dot strip along the bottom. Shows max 6 dots so the row
                  doesn't get crowded when there are many memories. */}
              <div className="absolute inset-x-0 bottom-1.5 z-10 flex items-center justify-center gap-1">
                {items.slice(0, 6).map((m, i) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setIdx(i)}
                    aria-label={`Slide ${i + 1}`}
                    className={cn(
                      "h-1 rounded-full transition-all",
                      i === idx ? "w-4 bg-white" : "w-1 bg-white/50",
                    )}
                  />
                ))}
                {items.length > 6 && (
                  <span className="text-[8px] font-semibold text-white/70">+{items.length - 6}</span>
                )}
              </div>
            </>
          )}
        </div>

        {/* Caption + event link sits BELOW the photo so it never overlaps
            the image — keeps the slide content fully visible. */}
        <Link
          to={`/events/${active.event_id}`}
          className="mt-2 block px-0.5 transition hover:text-primary"
        >
          <div className="flex items-center gap-1 text-xs font-semibold leading-tight line-clamp-1">
            {active.event_title}
            <ArrowUpRight className="h-3 w-3 shrink-0 opacity-60" />
          </div>
          {active.caption && (
            <div className="mt-0.5 line-clamp-1 text-[11px] text-muted-foreground">
              {active.caption}
            </div>
          )}
        </Link>
      </CardContent>
    </Card>
  );
}

function Slide({
  memory, visible,
}: {
  memory: EventMemoryRow;
  visible: boolean;
}) {
  return (
    <div
      aria-hidden={!visible}
      className={cn(
        "absolute inset-0 transition-opacity duration-500 ease-out",
        visible ? "opacity-100" : "pointer-events-none opacity-0",
      )}
    >
      {memory.image_url ? (
        <img
          src={memory.image_url}
          alt={memory.caption || memory.event_title}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full items-center justify-center text-muted-foreground">
          <ImageIcon className="h-6 w-6" />
        </div>
      )}
    </div>
  );
}
